import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Button,
  Typography,
  Link,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  IconButton,
  AppBar,
  Toolbar,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { fetchPendingLegalDocuments, acceptLegalDocuments } from 'src/redux/identity'
import TermsAndConditionsV1 from 'pages/Legal/TermsAndConditionsV1'
import TermsAndConditionsV2 from 'pages/Legal/TermsAndConditionsV2'
import PrivacyPolicyV1 from 'pages/Legal/PrivacyPolicyV1'
import PrivacyPolicyV2 from 'pages/Legal/PrivacyPolicyV2'

const LegalDocumentType = {
  TermsOfService: 1,
  PrivacyPolicy: 2,
}

const documentNames = {
  [LegalDocumentType.TermsOfService]: 'Terms of Service',
  [LegalDocumentType.PrivacyPolicy]: 'Privacy Policy',
}

const documentComponents = {
  [`${LegalDocumentType.TermsOfService}-1`]: TermsAndConditionsV1,
  [`${LegalDocumentType.TermsOfService}-2`]: TermsAndConditionsV2,
  [`${LegalDocumentType.PrivacyPolicy}-1`]: PrivacyPolicyV1,
  [`${LegalDocumentType.PrivacyPolicy}-2`]: PrivacyPolicyV2,
}

const AcceptLegalDocumentsModal = () => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const [viewingDocument, setViewingDocument] = useState(null)
  const { signedIn, pendingLegalDocuments = [], pendingLegalDocumentsError, acceptingLegalDocuments } = useSelector(state => state.identity)

  useEffect(() => {
    if (signedIn) {
      dispatch(fetchPendingLegalDocuments())
    }
  }, [signedIn, dispatch])

  const handleAccept = () => {
    dispatch(acceptLegalDocuments())
  }

  if (!signedIn) {
    return null
  }

  const dialogProps = {
    open: true,
    disableEscapeKeyDown: true,
    fullScreen,
    fullWidth: true,
    maxWidth: 'sm',
    sx: { zIndex: theme.zIndex.modal + 2 },
    slotProps: {
      backdrop: {
        sx: { backdropFilter: 'blur(2px)', backgroundColor: 'rgba(0, 0, 0, 0.3)' },
      },
    },
  }

  const titleSx = {
    color: 'primary.main',
    pt: 3,
    pb: 1,
    px: 3,
  }

  const dividerSx = {
    bgcolor: 'primary.main',
    height: '1px',
    borderRadius: '8px',
    mx: 3,
    width: '40%',
  }

  if (pendingLegalDocumentsError) {
    return (
      <Dialog {...dialogProps}>
        <DialogTitle sx={titleSx}>
          <Typography variant='h6'>Updated Legal Documents</Typography>
        </DialogTitle>
        <Divider sx={dividerSx} />
        <DialogContent sx={{ py: 3, px: 3 }}>
          <Typography variant='body1'>
            Something went wrong loading the terms. Please try again.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'grey.100', px: 3, py: 2 }}>
          <Button variant='contained' onClick={() => dispatch(fetchPendingLegalDocuments())}>
            Retry
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  if (pendingLegalDocuments.length === 0) {
    return null
  }

  const ViewingComponent = viewingDocument
    ? documentComponents[`${viewingDocument.documentType}-${viewingDocument.version}`]
    : null

  return (
    <>
      <Dialog {...dialogProps}>
        <DialogTitle sx={titleSx}>
          <Typography variant='h6'>Updated Legal Documents</Typography>
        </DialogTitle>
        <Divider sx={dividerSx} />
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Typography variant='body1'>
            Please review and accept the following to continue:
          </Typography>
          <List disablePadding>
            {pendingLegalDocuments.map(doc => (
              <ListItem key={doc.id} disableGutters>
                <ListItemText
                  primary={
                    <Link component='button' variant='body1' onClick={() => setViewingDocument(doc)}>
                      {documentNames[doc.documentType] || `Document ${doc.documentType}`} (v{doc.version})
                    </Link>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'grey.100', px: 3, py: 2 }}>
          <Button
            variant='contained'
            fullWidth
            onClick={handleAccept}
            disabled={acceptingLegalDocuments}
          >
            I Accept
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={viewingDocument !== null}
        onClose={() => setViewingDocument(null)}
        fullScreen
        sx={{ zIndex: theme.zIndex.modal + 3 }}
        slotProps={{
          backdrop: {
            sx: { backdropFilter: 'blur(2px)', backgroundColor: 'rgba(0, 0, 0, 0.3)' },
          },
        }}
      >
        <AppBar position='sticky' color='default' elevation={1} sx={{ bgcolor: 'white' }}>
          <Toolbar sx={{ justifyContent: 'flex-end' }}>
            <IconButton edge='end' onClick={() => setViewingDocument(null)} aria-label='close'>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        {ViewingComponent && <ViewingComponent />}
      </Dialog>
    </>
  )
}

export default AcceptLegalDocumentsModal
